/**
 * A guest's identity for ONE meeting: a random secret plus the name they typed, kept in localStorage so a
 * reconnect, a refresh or reopening the link later is recognised as the same guest (and an admitted guest
 * isn't sent back to the lobby). The server only ever uses the secret to say "same guest as before" — it
 * hashes it into the guest's id and never shows it to anyone.
 */
export interface GuestIdentity {
    token: string;
    name: string;
}

const key = (meetingId: string) => `athavita.connect.guest.v1.${meetingId.toLowerCase()}`;

export function loadGuestIdentity(meetingId: string): GuestIdentity | null {
    try {
        const raw = window.localStorage.getItem(key(meetingId));
        if (!raw) return null;
        const parsed = JSON.parse(raw) as Partial<GuestIdentity>;
        if (typeof parsed.token !== "string" || !/^[A-Za-z0-9_-]{16,128}$/.test(parsed.token)) return null;
        return { token: parsed.token, name: typeof parsed.name === "string" ? parsed.name : "" };
    } catch {
        return null;
    }
}

/** The stored identity with this name, creating the secret on first use. */
export function saveGuestIdentity(meetingId: string, name: string): GuestIdentity {
    const identity: GuestIdentity = { token: loadGuestIdentity(meetingId)?.token ?? randomToken(), name };
    try {
        window.localStorage.setItem(key(meetingId), JSON.stringify(identity));
    } catch {
        // Private mode: the identity lasts for this page only (a refresh is a new guest).
    }
    return identity;
}

export function randomToken(bytes = 24): string {
    const buf = new Uint8Array(bytes);
    crypto.getRandomValues(buf);
    let s = "";
    for (const b of buf) s += String.fromCharCode(b);
    return btoa(s).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}
